import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
    Button,
    Form,
    Input,
    Select,
    Table,
    Space,
    Modal,
    Popconfirm,
    message,
    Typography,
    Card,
    Row,
    Col,
} from "antd";
import {
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    PrinterOutlined,
    PlusOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import {
    getLawyersApi,
    createLawyerApi,
    updateLawyerApi,
    deleteLawyerApi,
} from "../../../api/master.api";

const { Title } = Typography;
const { Option } = Select;

const COURT_OPTIONS = [
    "আপিল বিভাগ এবং হাইকোর্ট বিভাগ",
    "আপিল বিভাগ",
    "হাইকোর্ট বিভাগ",
    "ঢাকা জেলা জজ আদালত",
    "চট্রগ্রাম জেলা জজ আদালত",
    "টাঙ্গাইল জেলা জজ আদালত",
    "মুন্সিগঞ্জ জেলা জজ আদালত",
];

const LAWYER_TYPE = "আইন উপদেষ্টা";

const LawAdvicer = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchName, setSearchName] = useState("");
    const [searchPhone, setSearchPhone] = useState("");
    const [searchCourt, setSearchCourt] = useState("");
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [createForm] = Form.useForm();
    const [editForm] = Form.useForm();

    // Convert English digits to Bengali
    const toBnDigits = (value) => {
        const en = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
        const bn = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];

        let strValue = String(value);
        const num = Number(value);
        if (!isNaN(num) && num >= 1 && num <= 9) {
            strValue = strValue.padStart(2, '0');
        }

        return strValue.replace(/[0-9]/g, d => bn[en.indexOf(d)]);
    };

    // 🔥 Fetch Lawyers
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await getLawyersApi(LAWYER_TYPE);
            const items = data.data || [];
            const normalized = items.map((item, idx) => ({
                key: item.lawyer_id || idx,
                ...item,
            }));
            setData(normalized);
        } catch (error) {
            message.error("ডাটা লোড করতে সমস্যা হয়েছে");
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Filtered data
    const filteredEntries = useMemo(() => {
        return data
            .filter((item) => (item.lawyer_type || "") === LAWYER_TYPE)
            .filter((item) =>
                (item.lawyer_name || "")
                    .toLowerCase()
                    .includes(searchName.trim().toLowerCase())
            )
            .filter((item) =>
                (item.mobile_no || "")
                    .toLowerCase()
                    .includes(searchPhone.trim().toLowerCase())
            )
            .filter((item) =>
                searchCourt ? (item.court_name || "") === searchCourt : true
            );
    }, [data, searchCourt, searchName, searchPhone]);

    // 🔥 Create
    const handleCreate = async (values) => {
        try {
            await createLawyerApi({
                ...values,
                lawyer_type: LAWYER_TYPE,
            });
            message.success("আইন উপদেষ্টা তৈরি সম্পন্ন");
            setIsCreateModalOpen(false);
            createForm.resetFields();
            fetchData();
        } catch (error) {
            message.error("তৈরি করতে ব্যর্থ হয়েছে");
            console.error(error);
        }
    };

    // 🔥 Update
    const handleEditSave = async (values) => {
        try {
            await updateLawyerApi(editing.lawyer_id, values);
            message.success("আপডেট সম্পন্ন");
            setIsEditModalOpen(false);
            setEditing(null);
            editForm.resetFields();
            fetchData();
        } catch (error) {
            message.error("আপডেট ব্যর্থ হয়েছে");
            console.error(error);
        }
    };

    // 🔥 Delete
    const handleDelete = async (record) => {
        try {
            await deleteLawyerApi(record.lawyer_id);
            message.success("ডিলিট সম্পন্ন");
            fetchData();
        } catch (error) {
            message.error("ডিলিট করতে ব্যর্থ");
            console.error(error);
        }
    };

    // Generate print content
    const generatePrintContent = () => {
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <style>
                    body {
                        margin: 0;
                        padding: 20px;
                        font-size: 14px;
                        line-height: 1.4;
                        font-family: 'Nikosh', 'NikoshBAN', 'SolaimanLipi', Arial, Helvetica, sans-serif;
                    }
                    .header {
                        text-align: center;
                        margin-bottom: 30px;
                    }
                    .header-text {
                        margin: 5px 0;
                        font-weight: bold;
                        font-size: 16px;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-top: 20px;
                    }
                    th, td {
                        border: 1px solid #000;
                        padding: 8px;
                        text-align: left;
                        vertical-align: top;
                        font-size: 12px;
                    }
                    th {
                        background-color: #f5f5f5;
                        font-weight: bold;
                        text-align: center;
                    }
                    .serial {
                        text-align: center;
                        width: 50px;
                    }
                    .court-info {
                        width: 200px;
                    }
                    @media print {
                        body { print-color-adjust: exact; }
                        .no-print { display: none; }
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <div class="header-text">বাংলাদেশ সেতু কর্তৃপক্ষের নির্বাচিত বিজ্ঞ আইন উপদেষ্টা</div>
                </div>
                <table>
                    <thead>
                        <tr>
                            <th class="serial">ক্রম</th>
                            <th>আদালতের নাম</th>
                            <th>আইন উপদেষ্টার নাম ও ঠিকানা</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${data.map((item, index) => `
                            <tr style="page-break-inside: avoid;">
                                <td class="serial">${toBnDigits(index + 1)}</td>
                                <td class="court-info">
                                    <div>সুপ্রীম কোর্টের</div>
                                    <div>আপিল বিভাগ ও হাইকোর্ট বিভাগ</div>
                                    <div>এবং</div>
                                    <div>জেলা জজ আদালতসমূহ</div>
                                </td>
                                <td>
                                    <div><strong>${item.lawyer_name}</strong></div>
                                    ${item.father_name && item.father_name !== "undefined" ? `<div>পিতা: ${item.father_name}</div>` : ""}
                                    ${item.permanent_address && item.permanent_address !== "undefined" ? `<div>স্থায়ী ঠিকানা: ${item.permanent_address}</div>` : ""}
                                    ${item.present_address && item.present_address !== "undefined" ? `<div>বর্তমান ঠিকানা: ${item.present_address}</div>` : ""}
                                    ${item.chamber_address && item.chamber_address !== "undefined" ? `<div>চেম্বার ঠিকানা: ${item.chamber_address}</div>` : ""}
                                    ${item.email && item.email !== "undefined" ? `<div>ইমেইল: ${item.email}</div>` : ""}
                                    ${item.mobile_no && item.mobile_no !== "undefined" ? `<div>মোবাইল: ${toBnDigits(item.mobile_no)}</div>` : ""}
                                </td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </body>
            </html>
        `;
    };

    const handlePrint = () => {
        const printContent = generatePrintContent();
        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.write(printContent);
            printWindow.document.close();
            printWindow.onload = () => {
                setTimeout(() => {
                    printWindow.print();
                }, 500);
            };
        }
    };

    // Table Columns
    const columns = [
        {
            title: "ক্রম",
            key: "serial",
            width: "5%",
            className: "case_column_bg",
            render: (_, __, index) => <div className="text-center">{toBnDigits(index + 1)}</div>
        },
        {
            title: "আদালতের নাম",
            key: "court_name",
            width: "25%",
            className: "case_column_bg",
            render: () => (
                <div className="text-center">
                    <div>সুপ্রীম কোর্টের</div>
                    <div>আপিল বিভাগ ও হাইকোর্ট বিভাগ</div>
                    <div>এবং</div>
                    <div>জেলা জজ আদালতসমূহ</div>
                </div>
            )
        },
        {
            title: "আইন উপদেষ্টার নাম ও ঠিকানা",
            key: "lawyer_name",
            className: "case_column_bg",
            render: (_, record) => (
                <div>
                    <div><strong>{record.lawyer_name}</strong></div>
                    {record.father_name && record.father_name !== "undefined" && (
                        <div>পিতা: {record.father_name}</div>
                    )}
                    {record.permanent_address && record.permanent_address !== "undefined" && (
                        <div>স্থায়ী ঠিকানা: {record.permanent_address}</div>
                    )}
                    {record.present_address && record.present_address !== "undefined" && (
                        <div>বর্তমান ঠিকানা: {record.present_address}</div>
                    )}
                    {record.chamber_address && record.chamber_address !== "undefined" && (
                        <div>চেম্বার ঠিকানা: {record.chamber_address}</div>
                    )}
                    {record.email && record.email !== "undefined" && (
                        <div>ইমেইল: {record.email}</div>
                    )}
                    {record.mobile_no && record.mobile_no !== "undefined" && (
                        <div>মোবাইল: {toBnDigits(record.mobile_no)}</div>
                    )}
                </div>
            ),
        },
        {
            title: "অ্যাকশন",
            key: "action",
            width: "10%",
            fixed: "right",
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        ghost
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditing(record);
                            editForm.setFieldsValue(record);
                            setIsEditModalOpen(true);
                        }}
                    />
                    <Popconfirm
                        title="ডিলিট নিশ্চিত করবেন?"
                        description="আপনি কি এই আইন উপদেষ্টাকে ডিলিট করতে চান?"
                        onConfirm={() => handleDelete(record)}
                        okText="হ্যাঁ"
                        cancelText="না"
                    >
                        <Button danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: "20px" }}>
            <Row justify="space-between" style={{ marginBottom: 20 }}>
                <Col>
                    <Title level={4}>আইন উপদেষ্টা তালিকা</Title>
                </Col>
                <Col>
                    <Space wrap>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchData}
                        >
                            রিফ্রেশ
                        </Button>
                        {/* <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setIsCreateModalOpen(true)}
                        >
                            নতুন আইন উপদেষ্টা
                        </Button> */}
                        <Button
                            type="danger"
                            icon={<PrinterOutlined />}
                            onClick={handlePrint}
                        >
                            প্রিন্ট
                        </Button>
                    </Space>
                </Col>
            </Row>

            {/* Filter Section */}
            <Card style={{ marginBottom: 20 }}>
                <Row gutter={16}>
                    <Col span={6}>
                        <Input
                            placeholder="নাম অনুসন্ধান"
                            prefix={<SearchOutlined />}
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            allowClear
                            size="large"
                        />
                    </Col>
                    <Col span={6}>
                        <Input
                            placeholder="ফোন অনুসন্ধান"
                            prefix={<SearchOutlined />}
                            value={searchPhone}
                            onChange={(e) => setSearchPhone(e.target.value)}
                            allowClear
                            size="large"
                        />
                    </Col>
                    <Col span={6}>
                        <Select
                            placeholder="কোর্ট ফিল্টার"
                            allowClear
                            value={searchCourt || undefined}
                            onChange={(v) => setSearchCourt(v || "")}
                            style={{ width: "100%" }}
                            size="large"
                        >
                            {COURT_OPTIONS.map((court) => (
                                <Option key={court} value={court}>
                                    {court}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={6}>
                        <Button
                            type="primary"
                            onClick={() => {
                                // Filter is already applied via useMemo
                            }}
                            size="large"
                        >
                            অনুসন্ধান করুন
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Table */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={filteredEntries}
                    loading={loading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total, range) =>
                            `${range[0]} থেকে ${range[1]} পর্যন্ত, মোট ${total} টি`,
                    }}
                    rowKey="lawyer_id"
                    bordered
                    scroll={{ x: 1024 }}
                />
            </Card>

            {/* CREATE MODAL */}
            <Modal
                title="নতুন আইন উপদেষ্টা তৈরি"
                open={isCreateModalOpen}
                onCancel={() => {
                    setIsCreateModalOpen(false);
                    createForm.resetFields();
                }}
                footer={null}
                width={800}
                destroyOnClose
            >
                <Form
                    form={createForm}
                    onFinish={handleCreate}
                    layout="vertical"
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="lawyer_name"
                                label="নাম"
                                rules={[{ required: true, message: "নাম প্রয়োজন" }]}
                            >
                                <Input placeholder="আইন উপদেষ্টার নাম লিখুন" size="large" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="father_name" label="পিতা/স্বামী">
                                <Input placeholder="পিতা/স্বামীর নাম" size="large" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="court_category" label="কোর্ট ক্যাটাগরি">
                                <Input placeholder="ক্যাটাগরি লিখুন" size="large" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="court_name"
                                label="কোর্টের নাম"
                                rules={[{ required: true, message: "কোর্টের নাম দিন" }]}
                            >
                                <Select placeholder="কোর্ট নির্বাচন করুন" size="large" allowClear>
                                    {COURT_OPTIONS.map((court) => (
                                        <Option key={court} value={court}>
                                            {court}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="permanent_address" label="স্থায়ী ঠিকানা">
                                <Input.TextArea rows={3} placeholder="স্থায়ী ঠিকানা" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="present_address" label="বর্তমান ঠিকানা">
                                <Input.TextArea rows={3} placeholder="বর্তমান ঠিকানা" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="chamber_address" label="চেম্বারের ঠিকানা">
                                <Input.TextArea rows={3} placeholder="চেম্বারের ঠিকানা" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="email" label="ইমেইল">
                                <Input type="email" placeholder="example@email.com" size="large" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="mobile_no"
                                label="মোবাইল"
                                rules={[{ required: true, message: "মোবাইল নম্বর দিন" }]}
                            >
                                <Input placeholder="01XXXXXXXXX" size="large" maxLength={11} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="status" label="স্ট্যাটাস" initialValue="active">
                                <Select size="large">
                                    <Option value="active">Active</Option>
                                    <Option value="inactive">Inactive</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="lawyer_type" label="ধরন" initialValue={LAWYER_TYPE}>
                                <Input disabled className="bg-white" size="large" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item>
                        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                            <Button onClick={() => setIsCreateModalOpen(false)}>
                                বাতিল
                            </Button>
                            <Button type="primary" htmlType="submit">
                                তৈরি করুন
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* EDIT MODAL */}
            <Modal
                title="আইন উপদেষ্টা সম্পাদনা"
                open={isEditModalOpen}
                onCancel={() => {
                    setIsEditModalOpen(false);
                    setEditing(null);
                    editForm.resetFields();
                }}
                footer={null}
                width={800}
                destroyOnClose
            >
                <Form
                    form={editForm}
                    onFinish={handleEditSave}
                    layout="vertical"
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="lawyer_name"
                                label="নাম"
                                rules={[{ required: true, message: "নাম প্রয়োজন" }]}
                            >
                                <Input placeholder="আইন উপদেষ্টার নাম লিখুন" size="large" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="father_name" label="পিতা/স্বামী">
                                <Input placeholder="পিতা/স্বামীর নাম" size="large" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="court_category" label="কোর্ট ক্যাটাগরি">
                                <Input placeholder="ক্যাটাগরি লিখুন" size="large" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="court_name"
                                label="কোর্টের নাম"
                                rules={[{ required: true, message: "কোর্টের নাম দিন" }]}
                            >
                                <Select placeholder="কোর্ট নির্বাচন করুন" size="large" allowClear>
                                    {COURT_OPTIONS.map((court) => (
                                        <Option key={court} value={court}>
                                            {court}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="permanent_address" label="স্থায়ী ঠিকানা">
                                <Input.TextArea rows={3} placeholder="স্থায়ী ঠিকানা" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="present_address" label="বর্তমান ঠিকানা">
                                <Input.TextArea rows={3} placeholder="বর্তমান ঠিকানা" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="chamber_address" label="চেম্বারের ঠিকানা">
                                <Input.TextArea rows={3} placeholder="চেম্বারের ঠিকানা" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="email" label="ইমেইল">
                                <Input type="email" placeholder="example@email.com" size="large" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="mobile_no"
                                label="মোবাইল"
                                rules={[{ required: true, message: "মোবাইল নম্বর দিন" }]}
                            >
                                <Input placeholder="01XXXXXXXXX" size="large" maxLength={11} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="status" label="স্ট্যাটাস">
                                <Select size="large">
                                    <Option value="active">Active</Option>
                                    <Option value="inactive">Inactive</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="lawyer_type" label="ধরন">
                                <Input disabled className="bg-white" size="large" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item>
                        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                            <Button onClick={() => setIsEditModalOpen(false)}>
                                বাতিল
                            </Button>
                            <Button type="primary" htmlType="submit">
                                আপডেট করুন
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default LawAdvicer;